import { read, utils, writeFile } from 'xlsx';
import type { ImportColumnDef } from './types';

export { getCell, getDataRowsAndMeta, toBool, toFloat, toInt } from 'api/customer/xlsxImportShared';

/**
 * Reads any spreadsheet-like file (xlsx/xls/csv, including the HTML-as-.xls files some
 * ERPs export) into a matrix of raw cells. Kept generic so the wizard can be reused for
 * every import flow, not just clients.
 */
export async function readSpreadsheetRows(file: File): Promise<unknown[][]> {
    const readTextWithEncoding = (f: File, encoding = 'windows-1255'): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsText(f, encoding);
        });

    const isCsvLike = /\.(csv|txt)$/i.test(file.name);
    const text = await readTextWithEncoding(file, 'windows-1255');
    let wb: ReturnType<typeof read>;
    if (text.startsWith('<HTML>') || text.includes('<TABLE')) {
        wb = read(text, { type: 'string' });
    } else if (isCsvLike) {
        wb = read(text, { type: 'string' });
    } else {
        const buffer = await file.arrayBuffer();
        wb = read(buffer, { type: 'array' });
    }
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = utils.sheet_to_json(ws, { header: 1, raw: false }) as unknown[][];
    return rows || [];
}

/** Generates and downloads a ready-to-fill example file matching an adapter's columns. */
export function downloadImportTemplate(columns: ImportColumnDef[], fileName: string) {
    const header = columns.map((c) => c.label);
    const exampleRow = columns.map((c) => (c.example !== undefined ? c.example : ''));
    const ws = utils.aoa_to_sheet([header, exampleRow]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'טעינה');
    writeFile(wb, fileName);
}

export function isEmptyRow(row: unknown[] | undefined): boolean {
    if (!row || row.length === 0) return true;
    return row.every((cell) => cell === undefined || cell === null || String(cell).trim() === '');
}
